/*
 * Tenants (audit Phase 3.3). Replaces wwwroot/js/Tenant/List.js, Members.js,
 * Applications.js and _namespace.js on the Phase 3.1 core. No jQuery.
 *
 * One screen: the Tenants of a Domain, and a detail dialog with three tabs --
 * the Tenant's own fields (saved with Save), its members, and the Applications
 * bound to it. Members and bindings write per row and take effect immediately,
 * so each reloads only its own tab.
 *
 * The legacy area bound its own document-level `shown.bs.tab` delegate and
 * carried a hand-rolled lazy loader beside it. core/tabs has done that for
 * every tab strip in the console since Phase 3.1 -- one delegated listener on
 * show.bs.tab, keyed off data-tab-url/data-loaded, which this dialog's markup
 * already carries -- so the copy went rather than moved.
 */
import { ajaxJson, ajaxModal, ajaxSwap, failToast, summaryOf } from '../core/ajax';
import { one } from '../core/dom';
import { serialize } from '../core/forms';
import { confirm, hideModalThen } from '../core/modal';
import { toast } from '../core/toast';
import { isValid, setValidationModelState } from '../core/validation';

const MODAL = 'TenantModal';
const CREATE_FORM = 'TenantCreateForm';
const EDIT_FORM = 'TenantEditForm';
const CREATE_ALERT = 'TenantCreateMessageContainer';
const EDIT_ALERT = 'TenantEditMessageContainer';

function currentDomainId(): string {
    return (one('#CurrentDomainId') as HTMLInputElement | null)?.value || '0';
}

/** The picker-backed hidden field for a tab's add control, as a positive id or 0. */
function pickedId(id: string): number {
    const parsed = Number.parseInt((one(`#${id}`) as HTMLInputElement | null)?.value ?? '', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/** The save button's icon swap; the dialog footers sit outside their forms. */
function saving(working: boolean): void {
    const modal = one(`#${MODAL}`);
    if (!modal) return;
    for (const node of Array.from(modal.querySelectorAll('.fa-save'))) node.classList.toggle('d-none', working);
    for (const node of Array.from(modal.querySelectorAll('.fa-spinner'))) node.classList.toggle('d-none', !working);
}

export const List = {
    ShowDetail(tenantId: number | string): void {
        void ajaxModal({
            modal: MODAL,
            url: `Tenant/Detail/${tenantId}`,
            title: null,
            // Naming the form is what attaches validation to it. Without this
            // isValid() built a validator with no rules and answered true for
            // everything, so an emptied Name reached the Api as a 400.
            formId: EDIT_FORM,
            errorTitle: `Tenant ${tenantId}`,
            errorMessage: 'Tenant Detail failed to execute:',
        });
    },

    NewTenant(): void {
        const domainId = currentDomainId();
        if (domainId === '0') {
            toast('warning', 'Select a Domain', 'A Tenant belongs to one Domain; pick one in the rail first.');
            return;
        }
        void ajaxModal({
            modal: MODAL,
            url: `Tenant/Create/${domainId}`,
            formId: CREATE_FORM,
            focusId: 'Name',
            errorTitle: 'New Tenant',
            errorMessage: 'Failed to render new tenant form',
        });
    },

    NewTenantSave(): void {
        const form = one(`#${CREATE_FORM}`) as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        saving(true);
        void ajaxJson({
            url: 'Tenant',
            method: 'POST',
            data: serialize(form),
            alertInto: CREATE_ALERT,
            errorTitle: 'Failed to create tenant',
            onSuccess: () => {
                toast('success', 'Tenant created');
                hideModalThen(MODAL, () => void List.Reload());
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                // The form, not the alert div beside it: only the form carries
                // the div.validation-summary the model-level entry goes into.
                if (modelState) setValidationModelState(modelState, `#${CREATE_FORM}`);
                toast('danger', 'Failed to create tenant', summaryOf(data));
                return true;
            },
            onComplete: () => saving(false),
        });
    },

    SaveDetail(tenantId: number | string): void {
        const form = one(`#${EDIT_FORM}`) as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        saving(true);
        void ajaxJson({
            url: `Tenant/${tenantId}`,
            method: 'PUT',
            data: serialize(form),
            alertInto: EDIT_ALERT,
            errorTitle: 'Failed to save tenant',
            onSuccess: () => {
                toast('success', 'Tenant saved');
                hideModalThen(MODAL, () => void List.Reload());
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                if (modelState) setValidationModelState(modelState, `#${EDIT_FORM}`);
                toast('danger', 'Failed to save tenant', summaryOf(data));
                return true;
            },
            onComplete: () => saving(false),
        });
    },

    DeleteTenant(tenantId: number | string): void {
        void confirm({
            title: 'Delete Tenant',
            html: 'Delete this Tenant?<br/><br/>'
                + '<span class="text-danger">Every Person membership and Application binding goes with it, '
                + 'so its members stop carrying a tenant_id claim and any multi-tenant App that keyed on it '
                + 'stops recognising them.</span>',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `Tenant/${tenantId}`,
                    method: 'DELETE',
                    alertInto: EDIT_ALERT,
                    errorTitle: 'Failed to delete tenant',
                    onSuccess: () => {
                        toast('success', 'Tenant deleted');
                        hideModalThen(MODAL, () => void List.Reload());
                    },
                    onFailure: failToast('Failed to delete tenant'),
                });
            },
        });
    },

    Reload(): Promise<void> {
        return ajaxSwap({
            container: 'ViewContainer',
            url: `Tenant/Domain/${currentDomainId()}`,
            alertTitle: 'Failed to reload tenants',
        });
    },
};

export const Members = {
    Add(tenantId: number | string): void {
        const personId = pickedId(`TenantAddPersonId_${tenantId}`);
        if (!personId) {
            toast('warning', 'Pick a Person', 'Use the search button to pick a Person before pressing Add.');
            return;
        }
        void ajaxJson({
            url: `Tenant/${tenantId}/Person/${personId}`,
            method: 'POST',
            alertInto: `TenantMembersMessageContainer_${tenantId}`,
            errorTitle: 'Failed to add member',
            onSuccess: () => {
                toast('success', 'Member added');
                void Members.Reload(tenantId);
            },
            onFailure: failToast('Failed to add member'),
        });
    },

    Remove(tenantId: number | string, personId: number | string): void {
        void confirm({
            title: 'Remove from Tenant',
            message: 'Remove this Person from the Tenant? Their Domain membership and other claims are '
                + 'unaffected, but they stop carrying this Tenant\'s id on their tokens.',
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `Tenant/${tenantId}/Person/${personId}`,
                    method: 'DELETE',
                    alertInto: `TenantMembersMessageContainer_${tenantId}`,
                    errorTitle: 'Failed to remove member',
                    onSuccess: () => {
                        toast('success', 'Member removed');
                        void Members.Reload(tenantId);
                    },
                    onFailure: failToast('Failed to remove member'),
                });
            },
        });
    },

    Reload(tenantId: number | string): Promise<void> {
        return ajaxSwap({
            container: 'TenantMembersTab',
            url: `Tenant/${tenantId}/Members`,
            alertTitle: 'Failed to reload members',
        });
    },
};

export const Applications = {
    Add(tenantId: number | string): void {
        const applicationId = pickedId(`TenantAddAppId_${tenantId}`);
        if (!applicationId) {
            toast('warning', 'Pick an Application', 'Use the search button to pick one before pressing Bind.');
            return;
        }
        void ajaxJson({
            url: `Tenant/${tenantId}/Application/${applicationId}`,
            method: 'POST',
            alertInto: `TenantAppsMessageContainer_${tenantId}`,
            errorTitle: 'Failed to bind Application',
            onSuccess: () => {
                toast('success', 'Application bound');
                void Applications.Reload(tenantId);
            },
            onFailure: failToast('Failed to bind Application'),
        });
    },

    Remove(tenantId: number | string, applicationId: number | string): void {
        void confirm({
            title: 'Unbind Application from Tenant',
            message: 'Unbind this Application from the Tenant? If the Application is multi-tenant, this '
                + 'Tenant\'s members can no longer sign in to it.',
            confirmText: 'Unbind',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `Tenant/${tenantId}/Application/${applicationId}`,
                    method: 'DELETE',
                    alertInto: `TenantAppsMessageContainer_${tenantId}`,
                    errorTitle: 'Failed to unbind Application',
                    onSuccess: () => {
                        toast('success', 'Application unbound');
                        void Applications.Reload(tenantId);
                    },
                    onFailure: failToast('Failed to unbind Application'),
                });
            },
        });
    },

    Reload(tenantId: number | string): Promise<void> {
        return ajaxSwap({
            container: 'TenantAppsTab',
            url: `Tenant/${tenantId}/Applications`,
            alertTitle: 'Failed to reload applications',
        });
    },
};
